import React, { useCallback, useEffect, useState } from 'react';
import AdminTopBar from '../../components/AdminTopBar';
import AdminSidebar from '../../components/AdminSidebar';
import { API_BASE_URL } from '../../config';

function authHeaders() {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * [Kullanım Kılavuzu] Admin sistem ipucu/duyuru nudge yönetimi.
 * Sayfa seç → nudge yaz → Düzenle/Sil/Yayınla/Yayından Kaldır + drag-drop sıralama.
 * Yayınlananlar seçilen sayfanın en üstünde (mavi) carousel olarak görünür.
 */
export default function AdminUsageGuide() {
  const [pages, setPages] = useState([]);
  const [pageKey, setPageKey] = useState('');
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', ctaPageKey: '', ctaLabel: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', body: '', ctaPageKey: '', ctaLabel: '' });
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Sayfa listesini çek
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/system-nudges/pages`, { headers: authHeaders(), credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setPages(data.pages || []);
          if (!pageKey && data.pages?.length) setPageKey(data.pages[0].key);
        }
      } catch (_e) { /* no-op */ }
    })();
    // eslint-disable-next-line
  }, []);

  const fetchNudges = useCallback(async (pk) => {
    if (!pk) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/system-nudges?pageKey=${encodeURIComponent(pk)}`, { headers: authHeaders(), credentials: 'include' });
      const data = await res.json();
      setNudges(data.success ? (data.nudges || []) : []);
    } catch (_e) {
      setNudges([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (pageKey) fetchNudges(pageKey); }, [pageKey, fetchNudges]);

  const apiCall = async (url, options = {}) => {
    const res = await fetch(`${API_BASE_URL}${url}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      ...options
    });
    return res.json();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    const data = await apiCall('/api/admin/system-nudges', {
      method: 'POST',
      body: JSON.stringify({ pageKey, title: form.title.trim(), body: form.body.trim(), ctaPageKey: form.ctaPageKey || null, ctaLabel: form.ctaLabel.trim() || null })
    });
    if (data.success) {
      setForm({ title: '', body: '', ctaPageKey: '', ctaLabel: '' });
      fetchNudges(pageKey);
    }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditForm({ title: n.title, body: n.body, ctaPageKey: n.ctaPageKey || '', ctaLabel: n.ctaLabel || '' }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({ title: '', body: '', ctaPageKey: '', ctaLabel: '' }); };
  const saveEdit = async (id) => {
    const data = await apiCall(`/api/admin/system-nudges/${id}`, { method: 'PUT', body: JSON.stringify(editForm) });
    if (data.success) { cancelEdit(); fetchNudges(pageKey); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu nudge silinsin mi?')) return;
    const data = await apiCall(`/api/admin/system-nudges/${id}`, { method: 'DELETE' });
    if (data.success) fetchNudges(pageKey);
  };

  const togglePublish = async (n) => {
    const action = n.status === 'published' ? 'unpublish' : 'publish';
    const data = await apiCall(`/api/admin/system-nudges/${n.id}/${action}`, { method: 'POST' });
    if (data.success) fetchNudges(pageKey);
  };

  // --- Drag reorder (native HTML5, AdminFaqManager deseni) ---
  const handleDrop = async (targetId) => {
    if (!draggingId || draggingId === targetId) { setDraggingId(null); setDragOverId(null); return; }
    const arr = [...nudges];
    const from = arr.findIndex((x) => x.id === draggingId);
    const to = arr.findIndex((x) => x.id === targetId);
    setDraggingId(null); setDragOverId(null);
    if (from < 0 || to < 0) return;
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setNudges(arr); // optimistic
    const items = arr.map((x, i) => ({ id: x.id, orderIndex: i }));
    await apiCall('/api/admin/system-nudges/reorder', { method: 'POST', body: JSON.stringify({ items }) });
    fetchNudges(pageKey);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 dark:bg-[#0d141c] dark:text-white">
      <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />
      <main className="flex min-h-screen flex-1 flex-col transition-[margin] duration-300 md:ml-[var(--admin-sidebar-width,18rem)]">
        <AdminTopBar title="Kullanım Kılavuzu" onToggleMobile={() => setMobileSidebarOpen((v) => !v)} />

        <div className="p-6 md:p-8 max-w-5xl mx-auto w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-gray-900 dark:text-white">Kullanım Kılavuzu</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Sayfaların en üstünde yayınlanacak sistem ipuçlarını/nudge'larını yönetin. Yayınlananlar seçilen sayfada mavi carousel olarak görünür.
        </p>
      </div>

      {/* Sayfa seçici */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <label className="text-sm font-bold text-gray-700 dark:text-gray-200">Sayfa:</label>
        <select
          value={pageKey}
          onChange={(e) => setPageKey(e.target.value)}
          className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        >
          {pages.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <span className="text-xs text-gray-400">{nudges.length} nudge</span>
      </div>

      {/* Yeni nudge formu */}
      <form onSubmit={handleCreate} className="mb-8 rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        <p className="mb-3 text-sm font-black uppercase tracking-wide text-gray-500">Yeni Nudge</p>
        <input
          type="text"
          placeholder="Başlık (örn. Günün Menüsü nedir?)"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className="mb-3 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />
        <textarea
          placeholder="İçerik / açıklama"
          value={form.body}
          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
          rows={3}
          className="mb-3 w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />
        {/* Opsiyonel CTA butonu (nudge sağında, sayfaya yönlendirir) */}
        <div className="mb-3 flex flex-col gap-2 sm:flex-row">
          <select
            value={form.ctaPageKey}
            onChange={(e) => setForm((f) => ({ ...f, ctaPageKey: e.target.value }))}
            className="rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">— Buton yok —</option>
            {pages.map((p) => <option key={p.key} value={p.key}>Buton → {p.label}</option>)}
          </select>
          <input
            type="text"
            placeholder="Buton metni (boşsa 'Git')"
            value={form.ctaLabel}
            onChange={(e) => setForm((f) => ({ ...f, ctaLabel: e.target.value }))}
            disabled={!form.ctaPageKey}
            className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <button type="submit" className="rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-white transition hover:brightness-110">
          Ekle (taslak)
        </button>
      </form>

      {/* Liste */}
      {loading ? (
        <p className="text-sm text-gray-400">Yükleniyor…</p>
      ) : nudges.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400 dark:border-gray-700">
          Bu sayfa için henüz nudge yok.
        </p>
      ) : (
        <div className="space-y-3">
          {nudges.map((n) => (
            <div
              key={n.id}
              draggable={editingId !== n.id}
              onDragStart={() => setDraggingId(n.id)}
              onDragEnter={() => { if (draggingId && draggingId !== n.id) setDragOverId(n.id); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(n.id)}
              onDragEnd={() => { setDraggingId(null); setDragOverId(null); }}
              className={`rounded-2xl border bg-white p-4 transition dark:bg-gray-800 ${
                dragOverId === n.id ? 'border-primary ring-2 ring-primary/30' : 'border-gray-200 dark:border-gray-700'
              } ${draggingId === n.id ? 'opacity-50' : ''}`}
            >
              {editingId === n.id ? (
                <div>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    className="mb-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  />
                  <textarea
                    value={editForm.body}
                    onChange={(e) => setEditForm((f) => ({ ...f, body: e.target.value }))}
                    rows={3}
                    className="mb-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  />
                  <div className="mb-2 flex flex-col gap-2 sm:flex-row">
                    <select
                      value={editForm.ctaPageKey}
                      onChange={(e) => setEditForm((f) => ({ ...f, ctaPageKey: e.target.value }))}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    >
                      <option value="">— Buton yok —</option>
                      {pages.map((p) => <option key={p.key} value={p.key}>Buton → {p.label}</option>)}
                    </select>
                    <input
                      type="text"
                      placeholder="Buton metni (boşsa 'Git')"
                      value={editForm.ctaLabel}
                      onChange={(e) => setEditForm((f) => ({ ...f, ctaLabel: e.target.value }))}
                      disabled={!editForm.ctaPageKey}
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(n.id)} className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white">Kaydet</button>
                    <button onClick={cancelEdit} className="rounded-lg border border-gray-300 px-4 py-1.5 text-xs font-bold text-gray-600 dark:border-gray-600 dark:text-gray-300">İptal</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2">
                    <span className="material-symbols-outlined mt-0.5 cursor-grab text-gray-300 dark:text-gray-600" title="Sürükle">drag_indicator</span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900 dark:text-white">{n.title}</p>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${
                          n.status === 'published'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {n.status === 'published' ? 'Yayında' : 'Taslak'}
                        </span>
                      </div>
                      <p className="mt-1 whitespace-pre-line text-sm text-gray-500 dark:text-gray-400">{n.body}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <button
                      onClick={() => togglePublish(n)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-bold text-white ${n.status === 'published' ? 'bg-amber-500' : 'bg-sky-600'}`}
                    >
                      {n.status === 'published' ? 'Yayından Kaldır' : 'Yayınla'}
                    </button>
                    <div className="flex gap-1.5">
                      <button onClick={() => startEdit(n)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-600 dark:border-gray-600 dark:text-gray-300">Düzenle</button>
                      <button onClick={() => handleDelete(n.id)} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 dark:border-red-900/40 dark:text-red-400">Sil</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
        </div>
      </main>
    </div>
  );
}
